/**
 * DRAFTING COMPONENTS (DRAFTING BASELINE round, 2026-10-01 — Matt: "Add some standard
 * drafting components like typical studs in sections, common nails and screws, steel
 * tubes (round and HSS) in some common sizes, steel angles in some common sizes, and a
 * breakline (scale-less like a symbol in Revit)").
 *
 * A component is placed as ordinary drafting linework/regions sharing one GROUP
 * (`groupId` + `componentId`) — so it selects as one unit, moves/copies/rotates/
 * mirrors/arrays/erases through the existing commands, snaps, prints, and exports
 * to DXF with no new record kind or renderer. UNGROUP releases it to plain lines.
 *
 * Geometry is TRUE SIZE in feet (the drafting surface's units) with the insertion
 * point at the origin, except the BREAK LINE, which is a paper-sized symbol: its jog
 * is set in PAPER inches and converted through the view's scale when placed (Revit's
 * scale-less symbol behaviour, frozen at placement — re-place it after changing a
 * view's scale). Dimensions are the published nominal/actual sizes (AISC for steel,
 * actual dressed lumber for studs, common-nail tables for fasteners).
 */
import type { DetailComponent, DraftingId, DraftingRecord, DraftLayerId, Path2D, Pt2 } from './types'
import type * as Y from 'yjs'
import { newNodeId } from './ids'
import { listDrafting, maps, recordToYMap, transact, type ModelDoc } from './doc'
import { newDraftingId } from './ids'
import { draftingForSurface, type DraftingScope } from './commands/drafting'

const IN = 1 / 12
const B90 = Math.tan(Math.PI / 8)   // bulge of a 90° arc (tan(sweep/4))

export interface ComponentPiece {
  kind: 'line' | 'region'
  pts: Pt2[]
  closed: boolean
  bulges?: (number | undefined)[]
  pen?: DetailComponent
  pattern?: string
}

export interface DraftComponentDef {
  id: string
  family: string
  label: string
  /** 'point' = one click at the insertion point; 'line' = two clicks (break line) */
  place: 'point' | 'line'
  /** where the insertion point sits, for the prompt */
  insertion: string
  /** local geometry — `opts.lenFt` (line components) and `opts.scaleInPerFt` (paper symbols) */
  build: (opts: BuildOpts) => ComponentPiece[]
  /**
   * COMPONENT LENGTH GRIP: a component whose LENGTH is a parameter (bolts, the break
   * line). The grip sits at `axis * (lenFt - offsetFt)` in local space; dragging it
   * along `axis` sets the length. `defaultFt` = the catalog length.
   */
  length?: { axis: Pt2; offsetFt: number; defaultFt: number; minFt: number }
  /**
   * BREAK LINE MASK (2026-10-13): a component that carries an adjustable MASKING band
   * on one side (the break line). `defaultIn` = its depth on PAPER at placement.
   */
  mask?: { defaultIn: number }
  /** COMPONENT VIEWS: the item this is one view of (absent = its own item), the view's
   *  key and name, and `hidden` = an earlier id kept only so placed records resolve */
  item?: string
  view?: string
  viewLabel?: string
  hidden?: boolean
}

/** what a component is built at: length (length components), the view scale (paper
 *  symbols), and the mask band's depth (local feet; 0 = none) and side (+1 = left of
 *  the component's +x direction, -1 = right) */
export interface BuildOpts {
  lenFt?: number; scaleInPerFt?: number
  /** BREAK LINE (local feet): mask depth above (+y) and below (-y) the line — 0 = none —
   *  and the jog's width (along the line) and height (off it); absent = paper defaults */
  maskFt?: number; maskBelowFt?: number; jogWFt?: number; jogHFt?: number
}

const rect = (w: number, h: number): Pt2[] => [[-w / 2, -h / 2], [w / 2, -h / 2], [w / 2, h / 2], [-w / 2, h / 2]]

/** a CCW rectangle with rounded corners (radius r; 0 = sharp) as pts + bulges */
function roundRect(w: number, h: number, r: number): { pts: Pt2[]; bulges: (number | undefined)[] } {
  const x0 = -w / 2, x1 = w / 2, y0 = -h / 2, y1 = h / 2
  if (r <= 1e-9) return { pts: rect(w, h), bulges: [undefined, undefined, undefined, undefined] }
  const pts: Pt2[] = [
    [x0 + r, y0], [x1 - r, y0], [x1, y0 + r], [x1, y1 - r],
    [x1 - r, y1], [x0 + r, y1], [x0, y1 - r], [x0, y0 + r],
  ]
  // the node BEFORE each corner arc carries its bulge (edge i → i+1)
  return { pts, bulges: [undefined, B90, undefined, B90, undefined, B90, undefined, B90] }
}

/** a full circle as two semicircle arcs (CCW) */
function circle(r: number): { pts: Pt2[]; bulges: (number | undefined)[] } {
  return { pts: [[r, 0], [-r, 0]], bulges: [1, 1] }
}

// ---- studs / lumber in section: the rectangle and its X (the continuous-member mark)
// (2026-10-13, Matt: "section cuts of lumber [should] have a thick outer edge border
// like a standard drafting convention for lines that are cut. The x … can be thin
// lines"): the cut outline is the HEAVY pen (was medium); the X stays fine
function lumber(actualW: number, actualH: number): ComponentPiece[] {
  const r = rect(actualW, actualH)
  return [
    { kind: 'line', pts: r, closed: true, pen: 'heavy' },
    { kind: 'line', pts: [r[0], r[2]], closed: false, pen: 'fine' },
    { kind: 'line', pts: [r[1], r[3]], closed: false, pen: 'fine' },
  ]
}

// ---- cold-formed steel studs (and, lipless, track) in section (2026-10-29, Matt: "We need metal studs, too,
// like Revit has showing them in section"): the cut C — web along y (like a lumber stud's
// depth), 1-5/8" flanges toward +x, 1/2" lips turned in, true steel thickness. Cut steel
// reads like the steel angle: a solid fill under the heavy cut outline, centered on the
// stud's bounding box. Sizes are SSMA designations (web × flange × mils).
function metalStud(depthIn: number, flangeIn: number, lipIn: number, tIn: number): ComponentPiece[] {
  const D = depthIn * IN, F = flangeIn * IN, L = lipIn * IN, t = tIn * IN
  const x0 = -F / 2, x1 = F / 2, y0 = -D / 2, y1 = D / 2
  // a TRACK (lipIn 0) is the plain U: the flange ends square, no lip
  const pts: Pt2[] = L > 0 ? [
    [x0, y0], [x1, y0], [x1, y0 + L], [x1 - t, y0 + L], [x1 - t, y0 + t], [x0 + t, y0 + t],
    [x0 + t, y1 - t], [x1 - t, y1 - t], [x1 - t, y1 - L], [x1, y1 - L], [x1, y1], [x0, y1],
  ] : [
    [x0, y0], [x1, y0], [x1, y0 + t], [x0 + t, y0 + t],
    [x0 + t, y1 - t], [x1, y1 - t], [x1, y1], [x0, y1],
  ]
  return [
    { kind: 'region', pts, closed: true, pattern: 'solid' },
    { kind: 'line', pts, closed: true, pen: 'heavy' },
  ]
}

// ---- fasteners in side view, laid along +x: the head at the origin, the point at +len
function nail(lenIn: number, shankIn: number, headIn: number): ComponentPiece[] {
  const L = lenIn * IN, s = shankIn * IN / 2, h = headIn * IN / 2, hd = 0.06 * IN, tip = s * 3
  return [
    { kind: 'line', pts: [[0, -h], [0, h], [hd, h], [hd, -h]], closed: true, pen: 'medium' },
    { kind: 'line', pts: [[hd, s], [L - tip, s], [L, 0], [L - tip, -s], [hd, -s]], closed: false, pen: 'medium' },
  ]
}
function screw(lenIn: number, shankIn: number, headIn: number, threadIn: number): ComponentPiece[] {
  const L = lenIn * IN, s = shankIn * IN / 2, h = headIn * IN / 2, tip = s * 4
  const pieces: ComponentPiece[] = [
    // countersunk head (flat top, conical underside)
    { kind: 'line', pts: [[0, -h], [0, h], [h - s, s], [h - s, -s]], closed: true, pen: 'medium' },
    { kind: 'line', pts: [[h - s, s], [L - tip, s], [L, 0], [L - tip, -s], [h - s, -s]], closed: false, pen: 'medium' },
  ]
  // threads: short slanted strokes along the threaded length (the drafting convention)
  const t0 = Math.max(h - s, L - threadIn * IN), pitch = Math.max(0.09 * IN, s * 1.6)
  for (let x = t0; x < L - tip; x += pitch) {
    pieces.push({ kind: 'line', pts: [[x, -s * 1.35], [x + pitch * 0.6, s * 1.35]], closed: false, pen: 'fine' })
  }
  return pieces
}

// ---- BOLTS (2026-10-01, Matt: "add commonly sized thru-bolts and anchor bolts")
// Proportions are the standard drafting ones (ANSI B18.2): hex across flats 1.5d,
// hex head height 2/3 d, heavy nut height 7/8 d, cut washer OD ~2.5d and 1/8" thick,
// thread length 2d + 1/4". Drawn in SIDE view; a hex shows its two facet lines.
function threads(x0: number, x1: number, s: number, vertical = false): ComponentPiece[] {
  const out: ComponentPiece[] = []
  const pitch = Math.max(0.09 * IN, s * 1.2)
  for (let t = x0; t < x1 - pitch * 0.5; t += pitch) {
    const a: Pt2 = [t, -s], b: Pt2 = [t + pitch * 0.6, s]
    out.push({ kind: 'line', closed: false, pen: 'fine', pts: vertical ? [[a[1], a[0]], [b[1], b[0]]] : [a, b] })
  }
  return out
}
/** a hex (head or nut) in side view: x from x0 to x0+t, across-flats w, centered on y=0 */
function hexSide(x0: number, t: number, w: number): ComponentPiece[] {
  const h = w / 2, f = w / 4
  return [
    { kind: 'line', closed: true, pen: 'medium', pts: [[x0, -h], [x0 + t, -h], [x0 + t, h], [x0, h]] },
    { kind: 'line', closed: false, pen: 'fine', pts: [[x0, -f], [x0 + t, -f]] },
    { kind: 'line', closed: false, pen: 'fine', pts: [[x0, f], [x0 + t, f]] },
  ]
}
const boxX = (x0: number, t: number, w: number, pen: DetailComponent = 'medium'): ComponentPiece =>
  ({ kind: 'line', closed: true, pen, pts: [[x0, -w / 2], [x0 + t, -w / 2], [x0 + t, w / 2], [x0, w / 2]] })

/** THROUGH-BOLT along +x: the head's bearing face at the origin, washer, shank, washer,
 *  nut, and the threads; `lenIn` is the nominal (under-head) length */
function thruBolt(dIn: number, lenIn: number): ComponentPiece[] {
  const d = dIn * IN, L = lenIn * IN, s = d / 2
  const wT = 0.125 * IN, wOD = 2.5 * d
  const nutT = 0.875 * d, headT = (2 / 3) * d, flats = 1.5 * d
  const stick = Math.max(0.25 * IN, d * 0.4)           // thread showing past the nut
  const nut0 = L - stick - nutT
  return [
    ...hexSide(-headT, headT, flats),
    boxX(0, wT, wOD),
    { kind: 'line', closed: false, pen: 'medium', pts: [[wT, s], [nut0 - wT, s]] },
    { kind: 'line', closed: false, pen: 'medium', pts: [[wT, -s], [nut0 - wT, -s]] },
    boxX(nut0 - wT, wT, wOD),
    ...hexSide(nut0, nutT, flats),
    { kind: 'line', closed: true, pen: 'medium', pts: [[nut0 + nutT, -s], [L, -s], [L, s], [nut0 + nutT, s]] },
    ...threads(Math.max(wT, L - (2 * dIn + 0.25) * IN), L, s),
  ]
}

/** how far an anchor bolt stands above the top of concrete, in inches (2x sill, the
 *  plate washer, the nut and the thread past it) — shared by the drawing and its grip */
function anchorProjectionIn(dIn: number): number {
  return 1.5 + 0.229 + 0.875 * dIn + Math.max(0.25, dIn * 0.4)
}

/** ANCHOR BOLT, vertical: the TOP OF CONCRETE at the origin. Below: the embedment and
 *  a 90-degree L hook (4d leg) or a hex head; above: a 2x sill (1-1/2"), the 3" square
 *  plate washer, the nut and the threads. `lenIn` = overall straight length. */
function anchorBolt(dIn: number, lenIn: number, kind: 'L' | 'headed'): ComponentPiece[] {
  const d = dIn * IN, r = d / 2, L = lenIn * IN
  const sill = 1.5 * IN, pwT = 0.229 * IN, pwW = 3 * IN
  const nutT = 0.875 * d, flats = 1.5 * d
  const stick = Math.max(0.25 * IN, d * 0.4)
  const proj = anchorProjectionIn(dIn) * IN
  const E = L - proj                                     // embedment below top of concrete
  const pieces: ComponentPiece[] = []
  // above the concrete: plate washer, nut (vertical side view), projecting end
  const yW = sill, yN = sill + pwT
  pieces.push({ kind: 'line', closed: true, pen: 'medium', pts: [[-pwW / 2, yW], [pwW / 2, yW], [pwW / 2, yW + pwT], [-pwW / 2, yW + pwT]] })
  pieces.push({ kind: 'line', closed: true, pen: 'medium', pts: [[-flats / 2, yN], [flats / 2, yN], [flats / 2, yN + nutT], [-flats / 2, yN + nutT]] })
  pieces.push({ kind: 'line', closed: false, pen: 'fine', pts: [[-flats / 4, yN], [-flats / 4, yN + nutT]] })
  pieces.push({ kind: 'line', closed: false, pen: 'fine', pts: [[flats / 4, yN], [flats / 4, yN + nutT]] })
  pieces.push({ kind: 'line', closed: true, pen: 'medium', pts: [[-r, yN + nutT], [r, yN + nutT], [r, proj], [-r, proj]] })
  // the rod from the plate washer down to the hook / head
  if (kind === 'L') {
    const leg = 4 * d
    pieces.push({
      kind: 'line', closed: false, pen: 'medium',
      pts: [[-r, yW], [-r, -E], [leg, -E], [leg, -E + d], [r, -E + d], [r, yW]],
    })
  } else {
    const headT = (2 / 3) * d
    pieces.push({ kind: 'line', closed: false, pen: 'medium', pts: [[-r, yW], [-r, -E + headT]] })
    pieces.push({ kind: 'line', closed: false, pen: 'medium', pts: [[r, yW], [r, -E + headT]] })
    pieces.push({ kind: 'line', closed: true, pen: 'medium', pts: [[-flats / 2, -E], [flats / 2, -E], [flats / 2, -E + headT], [-flats / 2, -E + headT]] })
    pieces.push({ kind: 'line', closed: false, pen: 'fine', pts: [[-flats / 4, -E], [-flats / 4, -E + headT]] })
    pieces.push({ kind: 'line', closed: false, pen: 'fine', pts: [[flats / 4, -E], [flats / 4, -E + headT]] })
  }
  pieces.push(...threads(proj - (2 * dIn + 0.25) * IN, proj, r, true))
  return pieces
}

// ---- BOLTS FROM ABOVE (2026-10-13, Matt: "add a bolt head from the top down view.
// Right now we have bolts only from the side"): the same ANSI B18.2 proportions as the
// side views, seen on the bolt's axis — a hex across flats 1.5d (flats top and bottom
// at 0°), its bearing-face chamfer circle (the across-flats circle every hex head and
// nut shows from above), and the cut washer under it (OD 2.5d). The NUT END shows the
// threaded end of the rod (d) standing inside the nut; the ANCHOR BOLT from above shows
// the side view's 3" square plate washer, its nut and the rod. Insertion = bolt axis.
function hexPlan(flats: number): ComponentPiece {
  const R = flats / Math.sqrt(3)            // circumradius (center → corner)
  const pts: Pt2[] = []
  for (let i = 0; i < 6; i++) { const a = (i * Math.PI) / 3; pts.push([R * Math.cos(a), R * Math.sin(a)]) }
  return { kind: 'line', closed: true, pen: 'medium', pts }
}
const circlePiece = (r: number, pen: DetailComponent): ComponentPiece => {
  const c = circle(r)
  return { kind: 'line', closed: true, pen, pts: c.pts, bulges: c.bulges }
}
function boltPlan(dIn: number, end: 'head' | 'nut', washer: boolean): ComponentPiece[] {
  const d = dIn * IN, flats = 1.5 * d
  const out: ComponentPiece[] = []
  if (washer) out.push(circlePiece(1.25 * d, 'fine'))             // cut washer, OD 2.5d
  out.push(hexPlan(flats))
  out.push(circlePiece(flats / 2, 'fine'))                         // bearing-face chamfer
  if (end === 'nut') out.push(circlePiece(d / 2, 'medium'))        // the rod's threaded end
  return out
}
// NAILS FROM ABOVE / BELOW (2026-10-13, Matt: "I also want nail heads so I can draw
// them from top or bottom views"): the same common-nail table as the side views. From
// the HEAD side: the head disc at the table's head diameter (the shank hides under
// it). From the POINT side: the shank circle with a small cross for the point's apex (the diamond point seen end on).
function nailPlan(shankIn: number, headIn: number, from: 'head' | 'point'): ComponentPiece[] {
  if (from === 'head') return [circlePiece((headIn * IN) / 2, 'medium')]
  const r = (shankIn * IN) / 2, c = r * 0.6
  return [
    circlePiece(r, 'medium'),
    { kind: 'line', closed: false, pen: 'fine', pts: [[-c, -c], [c, c]] },
    { kind: 'line', closed: false, pen: 'fine', pts: [[-c, c], [c, -c]] },
  ]
}
/** a SCREW from its head end: a wood screw's round head with its Phillips cross; a lag
 *  screw's hex (across flats 1.5d, as the bolts) with its chamfer circle */
function screwHeadPlan(shankIn: number, headIn: number, lag: boolean): ComponentPiece[] {
  if (lag) {
    const flats = 1.5 * shankIn * IN
    return [hexPlan(flats), circlePiece(flats / 2, 'fine')]
  }
  const r = (headIn * IN) / 2, c = r * 0.45
  return [
    circlePiece(r, 'medium'),
    { kind: 'line', closed: false, pen: 'fine', pts: [[-c, 0], [c, 0]] },
    { kind: 'line', closed: false, pen: 'fine', pts: [[0, -c], [0, c]] },
  ]
}
function anchorPlan(dIn: number): ComponentPiece[] {
  const d = dIn * IN, pw = 3 * IN
  return [
    { kind: 'line', closed: true, pen: 'medium', pts: rect(pw, pw) },  // 3" square plate washer
    hexPlan(1.5 * d),
    circlePiece(0.75 * d, 'fine'),
    circlePiece(d / 2, 'medium'),
  ]
}

// ---- steel in section
function pipe(odIn: number, wallIn: number): ComponentPiece[] {
  const o = circle(odIn * IN / 2), i = circle((odIn - 2 * wallIn) * IN / 2)
  return [
    { kind: 'line', pts: o.pts, bulges: o.bulges, closed: true, pen: 'heavy' },
    { kind: 'line', pts: i.pts, bulges: i.bulges, closed: true, pen: 'medium' },
  ]
}
function hss(bIn: number, hIn: number, tIn: number): ComponentPiece[] {
  // AISC design corner radii: outside 2t, inside t
  const t = tIn * IN
  const o = roundRect(bIn * IN, hIn * IN, 2 * t)
  const i = roundRect(bIn * IN - 2 * t, hIn * IN - 2 * t, t)
  return [
    { kind: 'line', pts: o.pts, bulges: o.bulges, closed: true, pen: 'heavy' },
    { kind: 'line', pts: i.pts, bulges: i.bulges, closed: true, pen: 'medium' },
  ]
}
function angle(legAIn: number, legBIn: number, tIn: number): ComponentPiece[] {
  // the heel at the origin, the long leg up +y, the short leg along +x
  const a = legAIn * IN, b = legBIn * IN, t = tIn * IN
  const pts: Pt2[] = [[0, 0], [b, 0], [b, t], [t, t], [t, a], [0, a]]
  return [
    { kind: 'region', pts, closed: true, pattern: 'solid' },
    { kind: 'line', pts, closed: true, pen: 'heavy' },
  ]
}

// ---- break line: a straight run with the standard Z jog at its middle, sized on PAPER.
// (2026-10-13, Matt: "thicker and an adjustable masking region on one side", then "in
// Revit the breakline symbol allows me to edit even the size of the breakline itself
// and I can grow all sides"): the MEDIUM pen; a MASKING band on EITHER side (above =
// +y, below = -y, each its own depth, 0 = none), its near edge following the line and
// its jog; and the jog's own WIDTH and HEIGHT. Every value is a grip (both ends, both
// masks, the jog's width and height) and a field in the left panel.
export const BREAK_JOG_W_IN = 0.125
export const BREAK_JOG_H_IN = 0.1875
function breakLine(lenFt: number, o: { scaleInPerFt: number; above: number; below: number; jogW: number; jogH: number }): ComponentPiece[] {
  const { above, below, jogW, jogH } = o
  const L = Math.max(lenFt, jogW * 1.5)
  const m = L / 2
  const pts: Pt2[] = [[0, 0], [m - jogW / 2, 0], [m - jogW / 6, jogH], [m + jogW / 6, -jogH], [m + jogW / 2, 0], [L, 0]]
  const out: ComponentPiece[] = []
  // a band shallower than the jog would fold over itself — it always clears the jog
  if (above > 1e-9) out.push({ kind: 'region', closed: true, pattern: 'mask', pts: [...pts, [L, Math.max(above, jogH * 1.2)], [0, Math.max(above, jogH * 1.2)]] })
  if (below > 1e-9) out.push({ kind: 'region', closed: true, pattern: 'mask', pts: [...pts, [L, -Math.max(below, jogH * 1.2)], [0, -Math.max(below, jogH * 1.2)]] })
  out.push({ kind: 'line', closed: false, pen: 'medium', pts })
  return out
}
/** a break line's parameters in LOCAL feet — its stored values over the paper defaults
 *  (a band placed in the one-sided round reads by its side) */
function breakParams(cp: NonNullable<DraftingRecord['componentPlace']> | null, scaleInPerFt: number) {
  const k = 1 / Math.max(scaleInPerFt, 1e-6)
  const legacyBelow = cp?.maskSide === -1
  return {
    above: legacyBelow ? 0 : (cp?.maskFt ?? BREAK_MASK_DEFAULT_IN * k),
    below: legacyBelow ? (cp?.maskFt ?? 0) : (cp?.maskBelowFt ?? 0),
    jogW: cp?.jogWFt ?? BREAK_JOG_W_IN * k,
    jogH: cp?.jogHFt ?? BREAK_JOG_H_IN * k,
  }
}
export const BREAK_MASK_DEFAULT_IN = 0.5

const LUMBER: [string, number, number][] = [
  ['2x4', 1.5, 3.5], ['2x6', 1.5, 5.5], ['2x8', 1.5, 7.25], ['2x10', 1.5, 9.25], ['2x12', 1.5, 11.25],
  ['4x4', 3.5, 3.5], ['4x6', 3.5, 5.5], ['6x6', 5.5, 5.5],
]
/** METAL TRACK (2026-10-29): the stud's runner top and bottom — [label, web depth, SSMA
 *  designation, thickness in], 1-1/4" flanges, no lips, the same gauges as the studs */
const METAL_TRACKS: [string, number, string, number][] = [
  ['3-5/8"', 3.625, '362T125-33', 0.0346], ['4"', 4, '400T125-33', 0.0346],
  ['6"', 6, '600T125-43', 0.0451], ['8"', 8, '800T125-54', 0.0566], ['10"', 10, '1000T125-54', 0.0566],
]
/** [label, web depth, SSMA designation, thickness in] — 1-5/8" flange, 1/2" lip */
const METAL_STUDS: [string, number, string, number][] = [
  ['3-5/8"', 3.625, '362S162-33', 0.0346], ['4"', 4, '400S162-33', 0.0346],
  ['6"', 6, '600S162-43', 0.0451], ['8"', 8, '800S162-54', 0.0566], ['10"', 10, '1000S162-54', 0.0566],
]
const NAILS: [string, number, number, number][] = [
  ['6d common (2")', 2, 0.113, 0.266], ['8d common (2-1/2")', 2.5, 0.131, 0.281],
  ['10d common (3")', 3, 0.148, 0.312], ['16d common (3-1/2")', 3.5, 0.162, 0.344],
  ['16d sinker (3-1/4")', 3.25, 0.148, 0.312],
]
const SCREWS: [string, number, number, number, number][] = [
  ['#8 × 1-1/4" wood screw', 1.25, 0.164, 0.332, 0.85],
  ['#10 × 2-1/2" wood screw', 2.5, 0.19, 0.385, 1.6],
  ['#10 × 3" wood screw', 3, 0.19, 0.385, 2],
  ['1/4" × 3" lag screw', 3, 0.25, 0.5, 2],
  ['3/8" × 4" lag screw', 4, 0.375, 0.75, 2.5],
]
const PIPES: [string, number, number][] = [
  ['1-1/2" Std pipe (1.900 OD)', 1.9, 0.145], ['2" Std pipe (2.375 OD)', 2.375, 0.154],
  ['3" Std pipe (3.500 OD)', 3.5, 0.216], ['4" Std pipe (4.500 OD)', 4.5, 0.237],
  ['HSS3.500×0.216', 3.5, 0.216], ['HSS4.500×0.237', 4.5, 0.237],
]
const HSS: [string, number, number, number][] = [
  ['HSS3×3×1/4', 3, 3, 0.25], ['HSS4×4×1/4', 4, 4, 0.25], ['HSS6×6×1/4', 6, 6, 0.25],
  ['HSS4×2×1/4', 4, 2, 0.25], ['HSS6×4×1/4', 6, 4, 0.25], ['HSS8×4×1/4', 8, 4, 0.25],
]
const ANGLES: [string, number, number, number][] = [
  ['L2×2×1/4', 2, 2, 0.25], ['L3×3×1/4', 3, 3, 0.25], ['L4×4×3/8', 4, 4, 0.375],
  ['L4×3×3/8', 4, 3, 0.375], ['L5×3×3/8', 5, 3, 0.375], ['L6×4×3/8', 6, 4, 0.375],
]
const THRU_BOLTS: [string, number, number][] = [
  ['1/2" × 6"', 0.5, 6], ['1/2" × 8"', 0.5, 8], ['1/2" × 10"', 0.5, 10],
  ['5/8" × 6"', 0.625, 6], ['5/8" × 8"', 0.625, 8], ['5/8" × 10"', 0.625, 10], ['5/8" × 12"', 0.625, 12],
  ['3/4" × 8"', 0.75, 8], ['3/4" × 10"', 0.75, 10], ['3/4" × 12"', 0.75, 12],
  // (2026-10-13) every diameter the bolt-end views come in has a side view too
  ['7/8" × 12"', 0.875, 12], ['1" × 12"', 1, 12],
]
const ANCHOR_BOLTS: [string, number, number, 'L' | 'headed'][] = [
  ['1/2" × 10" L-bolt', 0.5, 10, 'L'], ['5/8" × 10" L-bolt', 0.625, 10, 'L'],
  ['5/8" × 12" L-bolt', 0.625, 12, 'L'], ['3/4" × 12" L-bolt', 0.75, 12, 'L'],
  ['5/8" × 12" headed', 0.625, 12, 'headed'], ['3/4" × 12" headed', 0.75, 12, 'headed'],
  ['3/4" × 18" headed', 0.75, 18, 'headed'], ['7/8" × 24" headed', 0.875, 24, 'headed'],
]
const BOLT_PLAN_SIZES: [string, number][] = [
  ['1/2"', 0.5], ['5/8"', 0.625], ['3/4"', 0.75], ['7/8"', 0.875], ['1"', 1],
]
const ANCHOR_PLAN_SIZES: [string, number][] = [['1/2"', 0.5], ['5/8"', 0.625], ['3/4"', 0.75], ['7/8"', 0.875]]
const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

/**
 * COMPONENT VIEWS (2026-10-13, Matt: "instead of having the bolts show in two different
 * places in the list of details, when you click it you get the option to pick the view
 * you want (side, top, etc.) and this could be the case for all detail components we
 * might want to show in different configurations"): one ITEM in the list (a 5/8" × 10"
 * through-bolt, a 16d nail) with several VIEWS of it (side, head end, nut end…). Each
 * (item, view) is still its own def/id — that id is what a placed record stores, so
 * every component placed before this keeps rendering — and `item` / `view` tie them
 * together: the picker lists each item once and asks for the view, the left panel's
 * View choice rebuilds a placed component as another view of the same item. An item's
 * id is its FIRST view's id. Defs marked `hidden` are earlier ids kept only so placed
 * components still resolve; they never list.
 */
const view = (
  base: Omit<DraftComponentDef, 'id' | 'label' | 'build' | 'item' | 'view' | 'viewLabel' | 'insertion'>
    & { item: string; itemLabel: string },
  v: { key: string; label: string; idSuffix?: string; id?: string; insertion: string; build: DraftComponentDef['build']; length?: DraftComponentDef['length']; first?: boolean },
): DraftComponentDef => {
  const { itemLabel, ...rest } = base
  return {
    ...rest,
    id: v.id ?? (v.first ? base.item : `${base.item}-${v.idSuffix ?? v.key}`),
    label: v.first ? itemLabel : `${itemLabel} — ${v.label.toLowerCase()}`,
    view: v.key, viewLabel: v.label, insertion: v.insertion, build: v.build,
    ...(v.length ? { length: v.length } : {}),
  }
}

export const DRAFT_COMPONENTS: DraftComponentDef[] = [
  ...LUMBER.map(([n, w, h]): DraftComponentDef => ({
    id: `lumber-${slug(n)}`, family: 'Studs & lumber (section)', label: `${n} (${w}" × ${h}")`,
    place: 'point', insertion: 'center', build: () => lumber(w * IN, h * IN),
  })),
  ...METAL_STUDS.map(([n, d, ssma, t]): DraftComponentDef => ({
    id: `metal-stud-${slug(ssma)}`, family: 'Metal studs (section)', label: `${n} metal stud (${ssma})`,
    place: 'point', insertion: 'center', build: () => metalStud(d, 1.625, 0.5, t),
  })),
  ...METAL_TRACKS.map(([n, d, ssma, t]): DraftComponentDef => ({
    id: `metal-track-${slug(ssma)}`, family: 'Metal track (section)', label: `${n} metal track (${ssma})`,
    place: 'point', insertion: 'center', build: () => metalStud(d, 1.25, 0, t),
  })),
  ...NAILS.flatMap(([n, l, s, h]): DraftComponentDef[] => {
    const b = { item: `nail-${slug(n)}`, itemLabel: n, family: 'Nails', place: 'point' as const }
    return [
      view(b, { key: 'side', label: 'Side', first: true, insertion: 'head (points right)', build: () => nail(l, s, h) }),
      // ids from the plan-nail round (placed records keep resolving)
      view(b, { key: 'head', label: 'Head end', id: `nail-head-plan-${slug(n)}`, insertion: 'nail center', build: () => nailPlan(s, h, 'head') }),
      view(b, { key: 'point', label: 'Point end', id: `nail-point-plan-${slug(n)}`, insertion: 'nail center', build: () => nailPlan(s, h, 'point') }),
    ]
  }),
  ...SCREWS.flatMap(([n, l, s, h, t]): DraftComponentDef[] => {
    const b = { item: `screw-${slug(n)}`, itemLabel: n, family: 'Screws', place: 'point' as const }
    const lag = /lag/i.test(n)
    return [
      view(b, { key: 'side', label: 'Side', first: true, insertion: 'head (points right)', build: () => screw(l, s, h, t) }),
      view(b, { key: 'head', label: 'Head end', insertion: 'screw center', build: () => screwHeadPlan(s, h, lag) }),
    ]
  }),
  ...THRU_BOLTS.flatMap(([n, d, l]): DraftComponentDef[] => {
    const b = { item: `bolt-${slug(n)}`, itemLabel: `${n} machine bolt, washers + nut`, family: 'Through-bolts', place: 'point' as const }
    return [
      view(b, {
        key: 'side', label: 'Side', first: true, insertion: 'under the head (points right)',
        build: (o) => thruBolt(d, o.lenFt ? o.lenFt * 12 : l),
        length: { axis: [1, 0], offsetFt: 0, defaultFt: l / 12, minFt: (1.25 + 1.4 * d) / 12 },
      }),
      view(b, { key: 'head', label: 'Head end', insertion: 'bolt center', build: () => boltPlan(d, 'head', true) }),
      view(b, { key: 'headBare', label: 'Head end, no washer', idSuffix: 'head-nw', insertion: 'bolt center', build: () => boltPlan(d, 'head', false) }),
      view(b, { key: 'nut', label: 'Nut end', insertion: 'bolt center', build: () => boltPlan(d, 'nut', true) }),
    ]
  }),
  ...ANCHOR_BOLTS.flatMap(([n, d, l, k]): DraftComponentDef[] => {
    const b = { item: `anchor-${slug(n)}`, itemLabel: `${n} (2x sill, 3" plate washer)`, family: 'Anchor bolts', place: 'point' as const }
    return [
      view(b, {
        key: 'side', label: 'Side', first: true, insertion: 'top of concrete',
        build: (o) => anchorBolt(d, o.lenFt ? o.lenFt * 12 : l, k),
        // the grip is the EMBEDDED end (straight down from the top of concrete)
        length: { axis: [0, -1], offsetFt: anchorProjectionIn(d) / 12, defaultFt: l / 12, minFt: (anchorProjectionIn(d) + 2) / 12 },
      }),
      view(b, { key: 'top', label: 'From above', insertion: 'bolt center', build: () => anchorPlan(d) }),
    ]
  }),
  // the earlier "Bolts from above" ids (one per diameter) — hidden, kept so anything
  // already placed still draws; each belongs to the first item of its diameter
  ...BOLT_PLAN_SIZES.flatMap(([n, d]): DraftComponentDef[] => {
    const owner = THRU_BOLTS.find((x) => x[1] === d)
    const item = owner ? `bolt-${slug(owner[0])}` : undefined
    const base = { family: 'Through-bolts', place: 'point' as const, insertion: 'bolt center', hidden: true, item }
    return [
      { ...base, id: `bolt-head-plan-${slug(n)}`, label: `${n} hex head + washer`, view: 'head', viewLabel: 'Head end', build: () => boltPlan(d, 'head', true) },
      { ...base, id: `bolt-head-plan-nw-${slug(n)}`, label: `${n} hex head, no washer`, view: 'headBare', viewLabel: 'Head end, no washer', build: () => boltPlan(d, 'head', false) },
      { ...base, id: `bolt-nut-plan-${slug(n)}`, label: `${n} nut end + washer`, view: 'nut', viewLabel: 'Nut end', build: () => boltPlan(d, 'nut', true) },
    ]
  }),
  ...ANCHOR_PLAN_SIZES.map(([n, d]): DraftComponentDef => {
    const owner = ANCHOR_BOLTS.find((x) => x[1] === d)
    return {
      id: `anchor-plan-${slug(n)}`, family: 'Anchor bolts', label: `${n} anchor bolt, 3" plate washer + nut`,
      place: 'point', insertion: 'bolt center', build: () => anchorPlan(d),
      hidden: true, item: owner ? `anchor-${slug(owner[0])}` : undefined, view: 'top', viewLabel: 'From above',
    }
  }),
  ...PIPES.map(([n, od, t]): DraftComponentDef => ({
    id: `pipe-${slug(n)}`, family: 'Steel round tube / pipe', label: n,
    place: 'point', insertion: 'center', build: () => pipe(od, t),
  })),
  ...HSS.map(([n, b, h, t]): DraftComponentDef => ({
    id: `hss-${slug(n)}`, family: 'Steel HSS (rectangular)', label: n,
    place: 'point', insertion: 'center', build: () => hss(b, h, t),
  })),
  ...ANGLES.map(([n, a, b, t]): DraftComponentDef => ({
    id: `angle-${slug(n)}`, family: 'Steel angles', label: n,
    place: 'point', insertion: 'heel (outside corner)', build: () => angle(a, b, t),
  })),
  {
    id: 'break-line', family: 'Symbols', label: 'Break line',
    place: 'line', insertion: 'two clicks: start, end',
    build: ({ lenFt = 2, scaleInPerFt = 1.5, maskFt, maskBelowFt, jogWFt, jogHFt }) => {
      const k = 1 / Math.max(scaleInPerFt, 1e-6)
      return breakLine(lenFt, {
        scaleInPerFt,
        above: maskFt ?? BREAK_MASK_DEFAULT_IN * k, below: maskBelowFt ?? 0,
        jogW: jogWFt ?? BREAK_JOG_W_IN * k, jogH: jogHFt ?? BREAK_JOG_H_IN * k,
      })
    },
    length: { axis: [1, 0], offsetFt: 0, defaultFt: 2, minFt: 0.05 },
    mask: { defaultIn: BREAK_MASK_DEFAULT_IN },
  },
]
export const DRAFT_COMPONENT_BY_ID: Record<string, DraftComponentDef> =
  Object.fromEntries(DRAFT_COMPONENTS.map((d) => [d.id, d]))

/** COMPONENT VIEWS: a def's item id (its own id when it has no other views) */
export const componentItemOf = (def: DraftComponentDef): string => def.item ?? def.id

export interface DraftComponentItem {
  item: string
  family: string
  label: string
  place: 'point' | 'line'
  /** the item's views, in catalog order; a single entry = no view choice */
  views: { view: string; label: string; id: string }[]
}
/** the catalog as ITEMS — what the picker and the Type selector list (hidden defs never
 *  list; an item's label is its first view's) */
export const DRAFT_COMPONENT_ITEMS: DraftComponentItem[] = (() => {
  const out: DraftComponentItem[] = []
  const byItem = new Map<string, DraftComponentItem>()
  for (const d of DRAFT_COMPONENTS) {
    if (d.hidden) continue
    const key = componentItemOf(d)
    let it = byItem.get(key)
    if (!it) {
      it = { item: key, family: d.family, label: d.label, place: d.place, views: [] }
      byItem.set(key, it)
      out.push(it)
    }
    it.views.push({ view: d.view ?? 'default', label: d.viewLabel ?? 'Default', id: d.id })
  }
  return out
})()
export const DRAFT_COMPONENT_ITEM_BY_ID: Record<string, DraftComponentItem> =
  Object.fromEntries(DRAFT_COMPONENT_ITEMS.map((i) => [i.item, i]))

/** the component id for `item` shown as `view` — that view if the item has it, else the
 *  item's first view (a Type change keeps the view where it can) */
export function componentIdFor(item: string, viewKey?: string | null): string | null {
  const it = DRAFT_COMPONENT_ITEM_BY_ID[item]
  if (!it) return null
  return (viewKey && it.views.find((v) => v.view === viewKey)?.id) || it.views[0].id
}

/** a component's pieces moved/rotated into place (degrees CCW about `at`) */
export function placedComponentPieces(
  def: DraftComponentDef, at: Pt2, rotDeg: number, opts: BuildOpts = {},
): ComponentPiece[] {
  const r = (rotDeg * Math.PI) / 180, c = Math.cos(r), s = Math.sin(r)
  const xf = (p: Pt2): Pt2 => [at[0] + p[0] * c - p[1] * s, at[1] + p[0] * s + p[1] * c]
  return def.build(opts).map((pc) => ({ ...pc, pts: pc.pts.map(xf) }))
}

const newGroupId = () => 'dg_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36)

/** place a component as one group of drafting records; returns the new record ids */
export function placeDraftComponent(
  model: ModelDoc,
  input: {
    scope: DraftingScope; componentId: string; at: Pt2; rotDeg?: number
    lenFt?: number; scaleInPerFt?: number; layerId?: DraftLayerId | null
  },
): DraftingId[] {
  const def = DRAFT_COMPONENT_BY_ID[input.componentId]
  if (!def) return []
  const pieces = placedComponentPieces(def, input.at, input.rotDeg || 0, { lenFt: input.lenFt, scaleInPerFt: input.scaleInPerFt })
  const groupId = newGroupId()
  const ids: DraftingId[] = []
  const r = ((input.rotDeg || 0) * Math.PI) / 180
  const componentPlace = {
    at: [input.at[0], input.at[1]] as Pt2,
    ax: [Math.cos(r), Math.sin(r)] as Pt2,
    ay: [-Math.sin(r), Math.cos(r)] as Pt2,
    k: 1,
    lenFt: def.length ? (input.lenFt ?? def.length.defaultFt) : null,
    scaleInPerFt: input.scaleInPerFt ?? null,
    ...(def.mask ? (() => {
      const p = breakParams(null, input.scaleInPerFt ?? 1.5)
      return { maskFt: p.above, maskBelowFt: p.below, jogWFt: p.jogW, jogHFt: p.jogH }
    })() : {}),
  }
  transact(model, () => {
    const { drafting } = maps(model)
    for (const pc of pieces) {
      const id = newDraftingId()
      const path: Path2D = {
        closed: pc.closed,
        nodes: pc.pts.map((pt, i) => ({ id: newNodeId(), pt: [pt[0], pt[1]] as Pt2, ...(pc.bulges?.[i] ? { bulge: pc.bulges[i] } : {}) })),
      }
      const rec: Record<string, unknown> = {
        id, scope: input.scope, kind: pc.kind, path, groupId, componentId: def.id, componentPlace,
        ...(input.layerId ? { layerId: input.layerId } : {}),
      }
      if (pc.kind === 'line') rec.component = pc.pen || 'medium'
      else rec.pattern = pc.pattern || 'solid'
      drafting.set(id, recordToYMap(rec))
      ids.push(id)
    }
  })
  return ids
}

/**
 * COMPONENT LENGTH GRIP: a placed length component's grip — the world points of its
 * insertion (`base`) and its length end (`tip`), the world `dir` the length runs along,
 * and its current/min length. Null = not a length component, or placed before the round.
 */
export function componentLengthGrip(
  model: ModelDoc, groupId: string,
): { base: Pt2; tip: Pt2; dir: Pt2; lenFt: number; minFt: number; offsetFt: number; ids: DraftingId[] } | null {
  const recs = groupRecords(model, groupId)
  const cp = recs[0]?.componentPlace
  const def = recs[0]?.componentId ? DRAFT_COMPONENT_BY_ID[recs[0].componentId] : null
  if (!cp?.at || !def?.length) return null
  const k = cp.k ?? 1
  const toW = (p: Pt2): Pt2 => [cp.at[0] + k * (p[0] * cp.ax[0] + p[1] * cp.ay[0]), cp.at[1] + k * (p[0] * cp.ax[1] + p[1] * cp.ay[1])]
  const lenFt = cp.lenFt ?? def.length.defaultFt
  const a = def.length.axis
  const tip = toW([a[0] * (lenFt - def.length.offsetFt), a[1] * (lenFt - def.length.offsetFt)])
  const base = toW([0, 0])
  const dirW: Pt2 = [a[0] * cp.ax[0] + a[1] * cp.ay[0], a[0] * cp.ax[1] + a[1] * cp.ay[1]]
  return { base, tip, dir: dirW, lenFt: lenFt * k, minFt: def.length.minFt * k, offsetFt: def.length.offsetFt * k, ids: recs.map((r) => r.id) }
}

/**
 * COMPONENT LENGTH GRIP: rebuild a placed length component at `lenFt` (WORLD feet) in
 * place — same group, layer, frame and pens; one undo step. Returns the new ids.
 */
export function resizeDraftComponent(model: ModelDoc, groupId: string, lenFtWorld: number): DraftingId[] {
  const r0 = groupRecords(model, groupId)[0]
  const def = r0?.componentId ? DRAFT_COMPONENT_BY_ID[r0.componentId] : null
  if (!r0?.componentPlace?.at || !def?.length) return []
  return rebuildDraftComponent(model, groupId, { lenFtWorld })
}

/**
 * DETAIL COMPONENTS AS COMPONENTS (2026-10-13, Matt: "allow the user to select a detail
 * component after it's been placed and have it act like a real component that can be
 * copied and changed with the type selector … Research how Revit handles detail
 * components and provide parity"): one placed component = one INSTANCE — its
 * `componentId` is the TYPE (Revit's Family : Type) and `componentPlace` the placement.
 * This reads an instance back.
 */
export interface DraftComponentInstance {
  groupId: string
  componentId: string
  def: DraftComponentDef
  /** COMPONENT VIEWS: the item it is a view of, and which view (null = the item has one) */
  item: string
  view: string | null
  /** world insertion point, rotation (degrees CCW), mirrored frame, uniform scale */
  at: Pt2
  rotDeg: number
  mirrored: boolean
  k: number
  /** world length for a length component (bolts, the break line), else null */
  lenFt: number | null
  /** a break line's adjustable sizes in WORLD feet (mask depths 0 = none), else null */
  breakLine: { maskAboveFt: number; maskBelowFt: number; jogWFt: number; jogHFt: number } | null
  ids: DraftingId[]
}
export function draftComponentInstance(model: ModelDoc, groupId: string): DraftComponentInstance | null {
  const recs = groupRecords(model, groupId)
  const r0 = recs[0]
  const cp = r0?.componentPlace
  const def = r0?.componentId ? DRAFT_COMPONENT_BY_ID[r0.componentId] : null
  if (!r0 || !cp?.at || !def) return null
  const k = cp.k ?? 1
  return {
    groupId, componentId: def.id, def,
    item: componentItemOf(def), view: def.view ?? null,
    at: [cp.at[0], cp.at[1]],
    rotDeg: (Math.atan2(cp.ax[1], cp.ax[0]) * 180) / Math.PI,
    mirrored: cp.ax[0] * cp.ay[1] - cp.ax[1] * cp.ay[0] < 0,
    k,
    lenFt: def.length ? (cp.lenFt ?? def.length.defaultFt) * k : null,
    breakLine: def.mask ? (() => {
      // a break line placed before the mask existed reads as having none
      const p = breakParams(cp.maskFt == null && cp.maskBelowFt == null ? { ...cp, maskFt: 0 } : cp, cp.scaleInPerFt ?? 1.5)
      return { maskAboveFt: p.above * k, maskBelowFt: p.below * k, jogWFt: p.jogW * k, jogHFt: p.jogH * k }
    })() : null,
    ids: recs.map((r) => r.id),
  }
}

/** the placed component INSTANCES a drafting selection holds (each group once); a
 *  record that isn't part of a placed component counts as `others` */
export function draftComponentInstancesOf(model: ModelDoc, ids: DraftingId[]): { instances: DraftComponentInstance[]; others: number } {
  const byId = new Map(listDrafting(model).map((r) => [r.id, r]))
  const seen = new Set<string>()
  const instances: DraftComponentInstance[] = []
  let others = 0
  for (const id of ids) {
    const r = byId.get(id)
    if (!r) continue
    if (!r.groupId || !r.componentId) { others++; continue }
    if (seen.has(r.groupId)) continue
    seen.add(r.groupId)
    const inst = draftComponentInstance(model, r.groupId)
    if (inst) instances.push(inst)
    else others++
  }
  return { instances, others }
}

/** every placed instance of a component type among `records` (Revit's Select All
 *  Instances › Visible in View, given the view's records) */
export function draftComponentInstancesOfType(model: ModelDoc, records: DraftingRecord[], componentId: string): DraftComponentInstance[] {
  const groups = new Set<string>()
  for (const r of records) if (r.componentId === componentId && r.groupId) groups.add(r.groupId)
  return [...groups].map((g) => draftComponentInstance(model, g)).filter((x): x is DraftComponentInstance => !!x)
}

/**
 * Rebuild a placed component IN PLACE — the Revit type swap / instance edit: same
 * group, insertion point, layer and mirror state; one undo step. `componentId` swaps
 * the TYPE (point types swap among point types, the two-click break line only with its
 * own kind); `rotDeg` turns it about its insertion point; `lenFtWorld` sets a length
 * component's length. A length the user dragged off the old type's catalog length
 * carries over to the new type; an untouched one takes the new type's catalog length.
 * Returns the new record ids ([] = refused: not a component, placed before components
 * kept their placement, or the two types place differently).
 */
export function rebuildDraftComponent(
  model: ModelDoc, groupId: string,
  opts: {
    componentId?: string; rotDeg?: number; lenFtWorld?: number
    /** move the START end along the line by this much (world feet, + = shorter) */
    startShiftFtWorld?: number
    /** break line sizes, WORLD feet */
    breakLine?: { maskAboveFt?: number; maskBelowFt?: number; jogWFt?: number; jogHFt?: number }
  },
): DraftingId[] {
  const recs = groupRecords(model, groupId)
  const r0 = recs[0]
  const cp = r0?.componentPlace
  const oldDef = r0?.componentId ? DRAFT_COMPONENT_BY_ID[r0.componentId] : null
  const def = opts.componentId ? DRAFT_COMPONENT_BY_ID[opts.componentId] : oldDef
  if (!cp?.at || !oldDef || !def || def.place !== oldDef.place) return []
  const k = cp.k ?? 1
  let ax: Pt2 = cp.ax, ay: Pt2 = cp.ay
  const flip = ax[0] * ay[1] - ax[1] * ay[0] < 0   // a mirrored frame stays mirrored
  if (opts.rotDeg != null && Number.isFinite(opts.rotDeg)) {
    const r = (opts.rotDeg * Math.PI) / 180
    ax = [Math.cos(r), Math.sin(r)]
    ay = flip ? [Math.sin(r), -Math.cos(r)] : [-Math.sin(r), Math.cos(r)]
  }
  let lenFt: number | null = null
  if (def.length) {
    const cur = cp.lenFt ?? oldDef.length?.defaultFt ?? null
    const custom = cur != null && !!oldDef.length && Math.abs(cur - oldDef.length.defaultFt) > 1e-6
    lenFt = opts.lenFtWorld != null
      ? opts.lenFtWorld / k
      : (def.id === oldDef.id || custom || def.place === 'line') ? (cur ?? def.length.defaultFt) : def.length.defaultFt
    lenFt = Math.max(def.length.minFt, lenFt)
  }
  let at: Pt2 = cp.at
  if (opts.startShiftFtWorld && lenFt != null && def.length) {
    const sh = Math.min(opts.startShiftFtWorld / k, lenFt - def.length.minFt)
    at = [at[0] + ax[0] * sh * k, at[1] + ax[1] * sh * k]
    lenFt -= sh
  }
  const toW = (p: Pt2): Pt2 => [at[0] + k * (p[0] * ax[0] + p[1] * ay[0]), at[1] + k * (p[0] * ax[1] + p[1] * ay[1])]
  let extra: Record<string, unknown> = { maskFt: null, maskBelowFt: null, jogWFt: null, jogHFt: null, maskSide: null }
  let bo: BuildOpts = {}
  if (def.mask) {
    const p = breakParams(cp.maskFt == null && cp.maskBelowFt == null && cp.jogWFt == null ? { ...cp, maskFt: 0 } : cp, cp.scaleInPerFt ?? 1.5)
    const b = opts.breakLine || {}
    const W = (v: number | undefined, cur: number, min: number) => (v != null && Number.isFinite(v) ? Math.max(min, v / k) : cur)
    const jogW = W(b.jogWFt, p.jogW, 0.002)
    const jogH = W(b.jogHFt, p.jogH, 0)
    // a band never folds over the jog: a depth inside it lands at its edge (0 = none)
    const band = (v: number) => (v > 1e-9 ? Math.max(v, jogH * 1.2) : 0)
    const above = band(W(b.maskAboveFt, p.above, 0))
    const below = band(W(b.maskBelowFt, p.below, 0))
    if (lenFt != null) lenFt = Math.max(lenFt, jogW * 1.5)
    extra = { maskFt: above, maskBelowFt: below, jogWFt: jogW, jogHFt: jogH, maskSide: null }
    bo = { maskFt: above, maskBelowFt: below, jogWFt: jogW, jogHFt: jogH }
  }
  const pieces = def.build({ lenFt: lenFt ?? undefined, scaleInPerFt: cp.scaleInPerFt ?? undefined, ...bo })
  // KEEP ITS PLACE IN THE STACK (2026-10-13): the rebuilt pieces take the old pieces'
  // spot in the view's draw order (a type/size change used to lift it to the top)
  const surfRecs = draftingForSurface(model, r0.scope)
  const fill = surfRecs.some((x) => x.zOrder == null)
  const zOf = new Map(surfRecs.map((x, i) => [x.id, fill ? i : (x.zOrder as number)]))
  const own = new Set(recs.map((x) => x.id))
  const z0 = Math.min(...recs.map((x) => zOf.get(x.id) ?? Infinity))
  const ids: DraftingId[] = []
  transact(model, () => {
    const { drafting } = maps(model)
    if (fill) surfRecs.forEach((x, i) => { if (!own.has(x.id)) drafting.get(x.id)?.set('zOrder', i) })
    for (const r of recs) drafting.delete(r.id)
    for (const pc of pieces) {
      const id = newDraftingId()
      const path: Path2D = {
        closed: pc.closed,
        nodes: pc.pts.map((pt, i) => {
          const b = pc.bulges?.[i]
          return { id: newNodeId(), pt: toW(pt), ...(b ? { bulge: flip ? -b : b } : {}) }
        }),
      }
      const rec: Record<string, unknown> = {
        id, scope: r0.scope, kind: pc.kind, path, groupId, componentId: def.id,
        componentPlace: { ...cp, at, ax, ay, lenFt, ...extra },
        ...(r0.layerId ? { layerId: r0.layerId } : {}),
        ...(Number.isFinite(z0) ? { zOrder: z0 + ids.length / (pieces.length + 1) } : {}),
      }
      if (pc.kind === 'line') rec.component = pc.pen || 'medium'
      else rec.pattern = pc.pattern || 'solid'
      drafting.set(id, recordToYMap(rec))
      ids.push(id)
    }
  })
  return ids
}

/**
 * COMPONENT HANDLES (2026-10-13, Revit's break line: "I can grow all sides"): the grips a
 * placed component offers beyond its length end (componentLengthGrip, which snaps) —
 * for a break line: its START end, the mask band above and below, and the jog's width
 * and height. Each handle: `base` + unit `dir` (WORLD) — a drag projects the cursor
 * onto that ray, and the projection is the new `valueFt` (world feet, ≥ `minFt`);
 * `tip` = where the grip sits now. Apply with applyComponentHandle.
 */
export interface ComponentHandle { key: 'start' | 'maskAbove' | 'maskBelow' | 'jogW' | 'jogH'; base: Pt2; dir: Pt2; tip: Pt2; valueFt: number; minFt: number; label: string }
export function componentHandles(model: ModelDoc, groupId: string): ComponentHandle[] {
  const recs = groupRecords(model, groupId)
  const cp = recs[0]?.componentPlace
  const def = recs[0]?.componentId ? DRAFT_COMPONENT_BY_ID[recs[0].componentId] : null
  if (!cp?.at || !def?.mask || !def.length) return []
  const inst = draftComponentInstance(model, groupId)
  const bl = inst?.breakLine
  if (!bl) return []
  const k = cp.k ?? 1
  const L = (cp.lenFt ?? def.length.defaultFt) * k
  const ax = cp.ax, ay = cp.ay
  const P = (u: number, v: number): Pt2 => [cp.at[0] + u * ax[0] + v * ay[0], cp.at[1] + u * ax[1] + v * ay[1]]
  const neg = (d: Pt2): Pt2 => [-d[0], -d[1]]
  const mid = L / 2
  return [
    { key: 'start', base: P(L, 0), dir: neg(ax), tip: P(0, 0), valueFt: L, minFt: Math.max(def.length.minFt * k, bl.jogWFt * 1.5), label: 'Drag the start end (the other end stays)' },
    { key: 'maskAbove', base: P(L * 0.25, 0), dir: ay, tip: P(L * 0.25, bl.maskAboveFt), valueFt: bl.maskAboveFt, minFt: 0, label: 'Drag the mask on this side deeper or shallower — onto the line removes it' },
    { key: 'maskBelow', base: P(L * 0.75, 0), dir: neg(ay), tip: P(L * 0.75, -bl.maskBelowFt), valueFt: bl.maskBelowFt, minFt: 0, label: 'Drag the mask on this side deeper or shallower — onto the line removes it' },
    { key: 'jogW', base: P(mid, 0), dir: ax, tip: P(mid + bl.jogWFt / 2, 0), valueFt: bl.jogWFt / 2, minFt: 0.001, label: 'Drag to make the break jog wider or narrower' },
    { key: 'jogH', base: P(mid, 0), dir: ay, tip: P(mid, bl.jogHFt), valueFt: bl.jogHFt, minFt: 0, label: 'Drag to make the break jog taller or shorter' },
  ]
}
/** set one handle to `valueFt` (world feet) — one undo step; returns the new record ids */
export function applyComponentHandle(model: ModelDoc, groupId: string, key: ComponentHandle['key'], valueFt: number): DraftingId[] {
  const h = componentHandles(model, groupId).find((x) => x.key === key)
  if (!h) return []
  const v = Math.max(h.minFt, valueFt)
  if (key === 'start') return rebuildDraftComponent(model, groupId, { startShiftFtWorld: h.valueFt - v })
  if (key === 'maskAbove') return rebuildDraftComponent(model, groupId, { breakLine: { maskAboveFt: v } })
  if (key === 'maskBelow') return rebuildDraftComponent(model, groupId, { breakLine: { maskBelowFt: v } })
  if (key === 'jogW') return rebuildDraftComponent(model, groupId, { breakLine: { jogWFt: v * 2 } })
  return rebuildDraftComponent(model, groupId, { breakLine: { jogHFt: v } })
}

/** DETAIL COMPONENT SNAPS (2026-10-13, Matt: "snap to normal points in detail components
 *  — like midpoints of lumber sections"): each placed component's insertion point and the
 *  CENTER of every closed piece (a stud's middle, a washer's, a plate's), WORLD points.
 *  Corners, edge midpoints, arc centers and quadrants already come from the pieces. */
export function componentSnapPoints(records: DraftingRecord[]): { pt: Pt2; recId: DraftingId; key: string }[] {
  const out: { pt: Pt2; recId: DraftingId; key: string }[] = []
  const seen = new Set<string>()
  for (const r of records) {
    if (!r.groupId || !r.componentId) continue
    if (!seen.has(r.groupId) && r.componentPlace?.at) {
      seen.add(r.groupId)
      out.push({ pt: [r.componentPlace.at[0], r.componentPlace.at[1]], recId: r.id, key: 'ins' })
    }
    if (r.kind === 'line' && r.path?.closed && r.path.nodes.length >= 2) {
      const xs = r.path.nodes.map((n) => n.pt[0]), ys = r.path.nodes.map((n) => n.pt[1])
      // a circle is two arc nodes — their middle is its center
      out.push({ pt: [(Math.min(...xs) + Math.max(...xs)) / 2, (Math.min(...ys) + Math.max(...ys)) / 2], recId: r.id, key: 'ctr' })
    }
  }
  return out
}

/** LUMBER SECTION PEN (2026-10-13): studs and lumber placed before the cut outline went
 *  HEAVY take it now — the closed outline of a `lumber-*` component, medium → heavy.
 *  Firm-library details are left alone (they update through the firm library).
 *  Idempotent; returns the records changed. */
export function migrateLumberSectionPens(model: ModelDoc): number {
  const { drafting, views } = maps(model)
  const firmViews = new Set<string>()
  views.forEach((v, id) => { if ((v.get('scope') as { firmId?: string } | undefined)?.firmId) firmViews.add(id) })
  const todo: Y.Map<unknown>[] = []
  drafting.forEach((m) => {
    const cid = m.get('componentId')
    if (typeof cid !== 'string' || !cid.startsWith('lumber-') || m.get('kind') !== 'line' || m.get('component') !== 'medium') return
    if (!(m.get('path') as Path2D | undefined)?.closed) return
    const sc = m.get('scope') as { kind?: string; viewId?: string } | undefined
    if (sc?.kind === 'view' && sc.viewId && firmViews.has(sc.viewId)) return
    todo.push(m)
  })
  if (todo.length) transact(model, () => { for (const m of todo) m.set('component', 'heavy') })
  return todo.length
}

function groupRecords(model: ModelDoc, groupId: string): DraftingRecord[] {
  return listDrafting(model).filter((r) => r.groupId === groupId)
}
